/* eslint-disable sonarjs/cognitive-complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { setLoaderState } from '@tcp/core/src/components/common/molecules/Loader/container/Loader.actions';
import { trackFormError } from '@tcp/core/src/analytics/actions';
import { readCookie } from '@tcp/core/src/utils/cookie.util';
import { getDynamicLabelValues } from '@tcp/core/src/utils/labels/labelUtilty';
import logger from '@tcp/core/src/utils/loggerInstance';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import { setCvvInfoModuleX } from '@tcp/core/src/components/features/CnC/SinglePageCheckout/container/SinglePageCheckout.actions';
import { FETCH_CVV_MODULE_X } from '@tcp/core/src/components/features/CnC/SinglePageCheckout/container/SinglePageCheckout.constants';
import constants from './AddEditCreditCard.constants';
import { addCreditCardSuccess, addCreditCardError } from './AddEditCreditCard.actions';
import {
  addPaymentInstruction,
  updatePaymentInstruction,
} from '../../../../../services/abstractors/account/AddEditCreditCard';
import { getModuleX } from '../../../../../services/abstractors/common/moduleX';
import { getCreditDebitCards } from '../../Payment/container/Payment.selectors';
import { getAddressListState } from '../../AddressBook/container/AddressBook.selectors';
import { getUserEmail, getUserPhoneNumber } from '../../User/container/User.selectors';
import {
  clearGetAddressListTTL,
  getAddressList,
} from '../../AddressBook/container/AddressBook.actions';
import { clearCardListTTL } from '../../Payment/container/Payment.actions';
import { getAPIConfig, parseBoolean } from '../../../../../utils';
import {
  tokenizeCard,
  fetchPaySecureSessionId,
} from '../../../../../services/abstractors/CnC/PaySecure';
import { getUserInfo } from '../../User/container/User.actions';
import BAG_PAGE_ACTIONS from '../../../CnC/BagPage/container/BagPage.actions';

const getFormErrorPayload = (payload) => {
  const { cardNumber, expYear, expMonth, address } = payload;
  const { firstName, lastName, addressLine1, addressLine2, city, state, country, zipCode } =
    address;

  return {
    cardNumber,
    expYear,
    expMonth,
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    zipCode,
  };
};

export function* fetchPaySecureSessionIdSaga() {
  let response;
  try {
    response = yield call(fetchPaySecureSessionId);
  } catch (e) {
    logger.error({
      error: e,
      extraData: {
        methodName: 'PaySecure - getPaySecureSessionId',
      },
    });
  }

  return response;
}

export function* tokenizeCardAndGenerateSessionId(details) {
  const { sessionId, cardDetails, billingAddress } = details;
  const tokenResponse = yield call(tokenizeCard, sessionId, cardDetails, billingAddress);
  if (tokenResponse.SESSION_ID_HAS_BEEN_EXPIRED) {
    const res = yield call(fetchPaySecureSessionIdSaga, true);
    const newSessionId = res.SessionId;
    yield call(tokenizeCard, newSessionId, cardDetails, billingAddress);
  }
}

export function* recursiveTokenization(numberOfReattempts = 0, details) {
  const remainingAttempts = numberOfReattempts - 1;
  try {
    yield call(tokenizeCardAndGenerateSessionId, details);
  } catch (err) {
    // Retrying only when network call gets failed
    if (remainingAttempts >= 0 && err.networkStatusCode !== 200) {
      yield call(recursiveTokenization, remainingAttempts, details);
    } else {
      if (err.networkStatusCode !== 200) {
        logger.error({
          error: err,
          extraData: {
            methodName: 'PaySecure - Card Tokenization ',
            sessionId: details.sessionId,
            billingAddress: details.billingAddress,
          },
        });
      }
      throw err;
    }
  }
}

export function* paySecureOperations(cardDetails, billingAddress) {
  const disableTokenization = parseBoolean(getAPIConfig().disableTokenization);
  // Session id
  const res = yield call(fetchPaySecureSessionIdSaga);
  const sessionId = res.SessionId;
  const NUMBER_OF_RETRY_FOR_CARD_TOKENIZATION_API = 3;
  // Tokenize Card
  if (!disableTokenization) {
    yield call(recursiveTokenization, NUMBER_OF_RETRY_FOR_CARD_TOKENIZATION_API, {
      sessionId,
      cardDetails,
      billingAddress,
    });
  }

  return sessionId;
}
const getAddress = (addressEntry) => {
  return {
    addressLine1: addressEntry?.address1 || addressEntry?.addressLine1 || '',
    addressLine2: addressEntry?.address2 || addressEntry?.addressLine2 || '',
    city: addressEntry?.city,
    state: addressEntry?.stateCode || addressEntry?.state,
    country: addressEntry?.countryCode || addressEntry?.country || '',
    zipCode: addressEntry?.zipCode || addressEntry?.postalCode || '',
  };
};

// eslint-disable-next-line complexity
export function* addCreditCardSaga({ payload }) {
  yield put(setLoaderState(true));
  let isPlaceCardError = false;

  try {
    const {
      address,
      cardType,
      onFileAddressKey,
      isDefault,
      cardNumber,
      recaptchaToken,
      ...otherPayloadProps
    } = payload;
    const ccBook = yield select(getCreditDebitCards);
    const addressList = yield select(getAddressListState);

    const isEmptyAccount = !ccBook || ccBook.size === 0;
    let addressEntry;

    if (onFileAddressKey) {
      addressEntry = addressList.find((add) => add.addressId === onFileAddressKey);
    } else {
      addressEntry = Object.assign({}, address, {
        phoneNumber: yield select(getUserPhoneNumber),
        email: yield select(getUserEmail),
      });
    }

    if (!addressEntry.addressLine1 && addressEntry.addressLine) {
      const { addressLine } = addressEntry;
      [addressEntry.addressLine1, addressEntry.addressLine2] = addressLine;
    }

    const cardDetails = {
      accountNo: cardNumber,
      expMonth: otherPayloadProps?.expMonth,
      expYear: otherPayloadProps?.expYear,
      cvv: otherPayloadProps?.cvvCode,
    };
    const cardAddress = getAddress(addressEntry);
    let paySecureSessionId = '';
    if (!constants.PLCC_CARDS.includes(cardType))
      paySecureSessionId = yield call(paySecureOperations, cardDetails, cardAddress);
    const paymentInfo = {
      ...otherPayloadProps,
      ...addressEntry,
      isDefault: isDefault || isEmptyAccount,
      addressId: addressEntry ? addressEntry.addressId : null,
      cardType,
      cardNumber,
      saveToAccount: true,
      paySecureSessionId,
      recaptchaToken,
    };
    const isInternationalBilling =
      (addressEntry?.country !== 'US' &&
        addressEntry?.country !== 'CA' &&
        addressEntry?.country !== undefined) ||
      (addressEntry?.countryCode !== 'US' &&
        addressEntry?.countryCode !== 'CA' &&
        addressEntry?.countryCode !== undefined);

    if (constants.PLCC_CARDS.includes(cardType) && isInternationalBilling) {
      logger.error({
        error: 'PLCC Card Cannot have International Billing Address',
      });

      isPlaceCardError = true;
      throw new Error('_ERR_INTERNATIONAL_BILLING_ADDRESS_PLCC');
    }
    const response = yield call(addPaymentInstruction, paymentInfo);
    yield put(clearGetAddressListTTL());
    yield put(clearCardListTTL());
    yield put(getUserInfo({ ignoreCache: true }));
    const basketId = yield call(readCookie, 'basketId');
    if (basketId && response?.customerPaymentInstrument?.paymentMethodId === 'ADSPlaceCard') {
      yield put(BAG_PAGE_ACTIONS.getNonCachedCartData());
    }
    yield put(getAddressList());
    yield put(setLoaderState(false));
    return yield put(addCreditCardSuccess({ response }));
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        component: 'AddEditCreditCard Saga - addCreditCardSaga',
        payloadRecieved: payload,
      },
    });
    yield put(
      trackFormError({ formName: 'addEditCreditCard', formData: getFormErrorPayload(payload) })
    );
    yield put(setLoaderState(false));
    const ErrorForAdobe = 'form error:addoreditcreditcard';
    pushEventToAdobeAnalytics('form_error', ErrorForAdobe);
    if (isPlaceCardError) {
      const placeCardError = [
        {
          errorCode: '_ERR_INTERNATIONAL_BILLING_ADDRESS_PLCC',
          errorMessage: getDynamicLabelValues(
            'dynamic.error_messages.lbl_errorMessages_ERR_INTERNATIONAL_BILLING_ADDRESS_PLCC'
          ),
        },
      ];
      return yield put(addCreditCardError(placeCardError[0]));
    }
    if (err) {
      if (err[0]) {
        if (err[0].errorCode === '_TCP_PAYSECURE_PAYMENT_CC_TOKENIZATION_REQUEST_FAILED_ERROR') {
          const creditCardError = {
            errorCode: err[0].errorCode,
            errorMessage: getDynamicLabelValues(
              'dynamic.error_messages.lbl_errorMessages_ERR_ADD_EDIT_CREDIT_CARD'
            ),
          };
          return yield put(addCreditCardError(creditCardError));
        }
        return yield put(addCreditCardError(err[0]));
      }
      const error = {
        errorCode: 'INTERNAL_SERVER_ERROR',
        errorParameters: null,
      };
      yield put(addCreditCardError(error));
    }
    return null;
  }
}
// eslint-disable-next-line complexity
export function* updateCreditCardSaga({ payload }) {
  yield put(setLoaderState(true));
  let isPlaceCardError = false;
  try {
    const {
      address,
      cardType,
      onFileAddressKey,
      creditCardId,
      cardNumber,
      recaptchaToken,
      ...otherPayloadProps
    } = payload;
    const ccBook = yield select(getCreditDebitCards);
    const prevCreditCard = ccBook?.find((card) => card.creditCardId === creditCardId);
    const maskedNumber = cardNumber.startsWith('*');
    const addressList = yield select(getAddressListState);
    const cardDetails = {
      accountNo: cardNumber,
      expMonth: otherPayloadProps?.expMonth,
      expYear: otherPayloadProps?.expYear,
      cvv: otherPayloadProps?.cvvCode,
    };
    let addressEntry;
    if (onFileAddressKey) {
      addressEntry = addressList.find((add) => add.addressId === onFileAddressKey);
    } else {
      addressEntry = Object.assign({}, address, {
        phoneNumber: yield select(getUserPhoneNumber),
        email: yield select(getUserEmail),
      });
    }
    if (!addressEntry.addressLine1 && addressEntry.addressLine) {
      const { addressLine } = addressEntry;
      [addressEntry.addressLine1, addressEntry.addressLine2] = addressLine;
    }

    const cardAddress = getAddress(addressEntry);
    let paySecureSessionId;
    if (!maskedNumber && !constants.PLCC_CARDS.includes(cardType)) {
      paySecureSessionId = yield call(paySecureOperations, cardDetails, cardAddress);
    }
    const paymentInfo = {
      ...otherPayloadProps,
      ...addressEntry,
      prevCreditCard,
      addressId: addressEntry ? addressEntry.addressId : null,
      cardType,
      cardNumber,
      paySecureSessionId,
      creditCardId,
      maskedNumber,
      recaptchaToken,
    };
    const isInternationalBilling =
      (addressEntry?.country !== 'US' &&
        addressEntry?.country !== 'CA' &&
        addressEntry?.country !== undefined) ||
      (addressEntry?.countryCode !== 'US' &&
        addressEntry?.countryCode !== 'CA' &&
        addressEntry?.countryCode !== undefined);

    if (constants.PLCC_CARDS.includes(cardType) && isInternationalBilling) {
      logger.error({
        error: 'PLCC Card Cannot have International Billing Address',
      });

      isPlaceCardError = true;
      throw new Error('_ERR_INTERNATIONAL_BILLING_ADDRESS_PLCC');
    }
    const response = yield call(updatePaymentInstruction, paymentInfo);
    yield put(clearGetAddressListTTL());
    yield put(clearCardListTTL());
    yield put(getAddressList());
    yield put(setLoaderState(false));
    return yield put(addCreditCardSuccess({ response }));
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        component: 'AddEditCreditCard Saga - updateCreditCardSaga',
        payloadRecieved: payload,
      },
    });
    yield put(
      trackFormError({ formName: 'addEditCreditCard', formData: getFormErrorPayload(payload) })
    );
    yield put(setLoaderState(false));
    const ErrorForAdobe = 'form error:addoreditcreditcard';
    pushEventToAdobeAnalytics('form_error', ErrorForAdobe);
    if (isPlaceCardError) {
      const placeCardError = [
        {
          errorCode: '_ERR_INTERNATIONAL_BILLING_ADDRESS_PLCC',
          errorMessage: getDynamicLabelValues(
            'dynamic.error_messages.lbl_errorMessages_ERR_INTERNATIONAL_BILLING_ADDRESS_PLCC'
          ),
        },
      ];
      return yield put(addCreditCardError(placeCardError[0]));
    }

    if (err && err[0]) {
      const { errorCode } = err[0];
      const isTokenizationError =
        errorCode === '_TCP_PAYSECURE_PAYMENT_CC_TOKENIZATION_REQUEST_FAILED_ERROR';

      const creditCardError = {
        errorCode,
        errorMessage: isTokenizationError
          ? getDynamicLabelValues(
              'dynamic.error_messages.lbl_errorMessages_ERR_ADD_EDIT_CREDIT_CARD'
            )
          : err[0].errorMessage,
      };

      yield put(addCreditCardError(creditCardError));
    }
    return null;
  }
}

export function* fetchCvvInfoModuleX({ payload }) {
  try {
    const result = yield call(getModuleX, payload);
    yield put(setCvvInfoModuleX(result?.richText));
  } catch (err) {
    logger.error(err);
  }
}

export function* AddEditCreditCardSaga() {
  yield takeLatest(FETCH_CVV_MODULE_X, fetchCvvInfoModuleX);
  yield takeLatest(constants.ADD_CREDIT_CARD, addCreditCardSaga);
  yield takeLatest(constants.EDIT_CREDIT_CARD, updateCreditCardSaga);
}
export default AddEditCreditCardSaga;
